import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add12Filled,
  ArrowSortDownLinesRegular,
  ArrowSortUpLinesRegular,
  CheckmarkRegular,
  ChevronDown12Filled,
  GridDots20Regular,
  Person20Regular,
  PersonRegular,
  SlideMultiple20Regular,
  SlideMultipleRegular,
  StarFilled,
  StarRegular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { IWorkspace } from "models/IWorkspace";
import React, { useEffect, useRef, useState } from "react";
import {
  useSharedDividerStyles,
  useSharedPillButtonStyles,
  useSharedSortControlStyles,
} from "theme/sharedStyles";
import { useStyles } from "./ChatFilters.styles";
import { IChatFiltersProps } from "./ChatFilters.types";

export const ChatFilters = ({
  workspaces,
  selectedWorkspaceId,
  onWorkspaceChange,
  isFavorited,
  onFavoritedChange,
  isAscending,
  onSortChange,
  sortField,
  onSortFieldChange,
  conversationCount,
  isLoading = false,
  onNewChat,
  newChatTargetWorkspaceName,
}: IChatFiltersProps): JSX.Element => {
  const styles = useStyles();
  const sharedPillButtonStyles = useSharedPillButtonStyles();
  const sharedDividerStyles = useSharedDividerStyles();
  const sortStyles = useSharedSortControlStyles();
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] =
    useState<boolean>(false);
  const [focusedMenuItemIndex, setFocusedMenuItemIndex] = useState<number>(-1);
  const workspaceTriggerRef = useRef<HTMLButtonElement>(null);
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedWorkspace = workspaces.find(
    (w) => w.id === selectedWorkspaceId,
  );
  const workspaceDisplayLabel =
    selectedWorkspaceId === undefined
      ? ConstantValues.ALL_WORKSPACES
      : selectedWorkspace?.name || "Select Workspace";

  const getMenuItems = () => {
    const items: Array<{
      id: string | undefined;
      name: string;
      workspace?: IWorkspace;
    }> = [
      {
        id: undefined,
        name: ConstantValues.ALL_WORKSPACES,
      },
      {
        id: workspaces.find((w) => w.name === ConstantValues.PERSONAL_WORKSPACE)
          ?.id,
        name: ConstantValues.PERSONAL_WORKSPACE,
        workspace: workspaces.find(
          (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
        ),
      },
      ...workspaces
        .filter((w) => w.name !== ConstantValues.PERSONAL_WORKSPACE)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((ws) => ({ id: ws.id, name: ws.name, workspace: ws })),
    ];
    return items;
  };

  const menuItems = getMenuItems();

  useEffect(() => {
    try {
      const savedWorkspaceId = localStorage.getItem(
        ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID,
      );
      const savedAscending = localStorage.getItem(
        ConstantValues.MY_CHATS_FILTER_ASCENDING,
      );
      const savedSortField = localStorage.getItem(
        ConstantValues.MY_CHATS_FILTER_SORT_FIELD,
      );

      if (savedWorkspaceId && savedWorkspaceId !== "undefined") {
        onWorkspaceChange(savedWorkspaceId);
      }
      if (savedAscending === "true") {
        onSortChange(true);
      }
      if (savedSortField === "name" || savedSortField === "updatedAt") {
        onSortFieldChange(savedSortField);
      }
    } catch (error) {
      console.error("Failed to load filter state from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    try {
      if (selectedWorkspaceId === undefined) {
        localStorage.removeItem(ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID);
      } else {
        localStorage.setItem(
          ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID,
          selectedWorkspaceId,
        );
      }

      if (isAscending) {
        localStorage.setItem(ConstantValues.MY_CHATS_FILTER_ASCENDING, "true");
      } else {
        localStorage.removeItem(ConstantValues.MY_CHATS_FILTER_ASCENDING);
      }

      localStorage.setItem(
        ConstantValues.MY_CHATS_FILTER_SORT_FIELD,
        sortField,
      );
    } catch (error) {
      console.error("Failed to save filter state to localStorage:", error);
    }
  }, [selectedWorkspaceId, isAscending, sortField]);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.contentEditable === "true";

      if (isInput) return;

      switch (e.key.toLowerCase()) {
        case "f": {
          if (!isLoading) {
            e.preventDefault();
            workspaceTriggerRef.current?.focus();
            setIsWorkspaceDropdownOpen(true);
          }
          break;
        }
        case "p": {
          if (!isLoading && !isWorkspaceDropdownOpen) {
            e.preventDefault();
            onFavoritedChange(!isFavorited);
          }
          break;
        }
        case "s": {
          if (!isLoading && !isWorkspaceDropdownOpen) {
            e.preventDefault();
            onSortChange(!isAscending);
          }
          break;
        }
        case "escape": {
          if (isWorkspaceDropdownOpen) {
            setIsWorkspaceDropdownOpen(false);
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isLoading, isFavorited, isAscending, isWorkspaceDropdownOpen]);

  const handleWorkspaceSelect = (workspaceId: string | undefined) => {
    onWorkspaceChange(workspaceId);
    setIsWorkspaceDropdownOpen(false);
  };

  const handleMenuKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, menuItems.length - 1);
        setFocusedMenuItemIndex(nextIndex);
        menuItemsRef.current[nextIndex]?.focus();
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prevIndex = Math.max(index - 1, 0);
        setFocusedMenuItemIndex(prevIndex);
        menuItemsRef.current[prevIndex]?.focus();
        break;
      }
      case "Enter":
      case " ": {
        e.preventDefault();
        const item = menuItems[index];
        if (item) {
          handleWorkspaceSelect(item.id);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setIsWorkspaceDropdownOpen(false);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isWorkspaceDropdownOpen) {
      workspaceTriggerRef.current?.focus();
      if (focusedMenuItemIndex !== -1) {
        setFocusedMenuItemIndex(-1);
      }
    }
  }, [isWorkspaceDropdownOpen, focusedMenuItemIndex]);

  useEffect(() => {
    if (isWorkspaceDropdownOpen) {
      setFocusedMenuItemIndex(-1);
    }
  }, [isWorkspaceDropdownOpen]);

  const renderMenuItem = (
    item: { id: string | undefined; name: string; workspace?: IWorkspace },
    index: number,
  ) => {
    const isAllWorkspaces = item.name === ConstantValues.ALL_WORKSPACES;
    const isPersonalWorkspace = item.name === ConstantValues.PERSONAL_WORKSPACE;
    const isSelected = selectedWorkspaceId === item.id;

    const icon = isAllWorkspaces ? (
      <GridDots20Regular className={styles.menuItemIcon} />
    ) : isPersonalWorkspace ? (
      <PersonRegular className={styles.menuItemIcon} />
    ) : (
      <SlideMultipleRegular className={styles.menuItemIcon} />
    );

    const isFirstSharedWorkspace =
      !isAllWorkspaces &&
      !isPersonalWorkspace &&
      index > 0 &&
      (menuItems[index - 1]?.name === ConstantValues.PERSONAL_WORKSPACE ||
        menuItems[index - 1]?.name === ConstantValues.ALL_WORKSPACES);
    const showDivider = isPersonalWorkspace || isFirstSharedWorkspace;

    return (
      <div key={item.id ?? "all-workspaces"}>
        {showDivider && (
          <div
            className={styles.sectionDivider}
            role="separator"
            aria-hidden="true"
          />
        )}
        <button
          ref={(el) => {
            menuItemsRef.current[index] = el;
          }}
          className={mergeClasses(
            styles.menuItem,
            isSelected && styles.menuItemSelected,
          )}
          onClick={() => handleWorkspaceSelect(item.id)}
          onKeyDown={(e) => handleMenuKeyDown(e, index)}
          role="option"
          aria-selected={isSelected}
          tabIndex={focusedMenuItemIndex === index ? 0 : -1}
        >
          {icon}
          <Tooltip
            content={item.name}
            relationship="label"
            showDelay={300}
            hideDelay={100}
            positioning="after"
          >
            <span className={styles.menuItemLabel}>{item.name}</span>
          </Tooltip>
          {isSelected && <CheckmarkRegular className={styles.checkmark} />}
        </button>
      </div>
    );
  };

  return (
    <div className={styles.filterBarWrapper}>
      <div className={styles.filterBar} role="group" aria-label="Chat filters">
        <div className={styles.leftSection}>
          <div
            className={mergeClasses(
              styles.countContainer,
              isLoading && styles.countLoading,
            )}
            aria-live="polite"
            aria-label={`${conversationCount} chats`}
          >
            <span className={styles.countBold}>{conversationCount}</span>
            <span className={styles.countLabel}>chats</span>
          </div>
        </div>

        <div
          className={mergeClasses(
            sharedDividerStyles.vertical,
            styles.verticalDivider,
          )}
          aria-hidden="true"
        />

        <div className={styles.rightSection}>
          <div className={styles.workspaceGroup}>
            <Popover
              open={isWorkspaceDropdownOpen}
              onOpenChange={(_, data) => setIsWorkspaceDropdownOpen(data.open)}
              positioning={{
                position: "below",
                align: "start",
                autoSize: "width",
              }}
            >
              <PopoverTrigger disableButtonEnhancement>
                <Tooltip
                  content={workspaceDisplayLabel}
                  relationship="label"
                  showDelay={300}
                  hideDelay={100}
                >
                  <button
                    ref={workspaceTriggerRef}
                    className={styles.workspaceButton}
                    disabled={isLoading}
                    aria-label={`Filter by workspace: ${workspaceDisplayLabel}`}
                    aria-expanded={isWorkspaceDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    {selectedWorkspaceId === undefined ? (
                      <GridDots20Regular
                        className={styles.workspaceIconInner}
                      />
                    ) : selectedWorkspace?.name ===
                      ConstantValues.PERSONAL_WORKSPACE ? (
                      <Person20Regular className={styles.workspaceIconInner} />
                    ) : (
                      <SlideMultiple20Regular
                        className={styles.workspaceIconInner}
                      />
                    )}
                    <span className={styles.workspaceLabel}>
                      {workspaceDisplayLabel}
                    </span>
                    <ChevronDown12Filled
                      className={mergeClasses(
                        styles.chevron,
                        isWorkspaceDropdownOpen && styles.chevronOpen,
                      )}
                    />
                  </button>
                </Tooltip>
              </PopoverTrigger>

              <PopoverSurface className={styles.popoverSurface}>
                <div
                  className={styles.menu}
                  role="listbox"
                  aria-label="Select workspace"
                >
                  {menuItems.map((item, index) => renderMenuItem(item, index))}
                </div>
              </PopoverSurface>
            </Popover>

            <div
              className={mergeClasses(
                sharedDividerStyles.vertical,
                styles.workspaceGroupDivider,
              )}
              aria-hidden="true"
            />

            <Tooltip
              content="Start a new chat"
              relationship="description"
              showDelay={300}
              hideDelay={100}
            >
              <button
                className={styles.newChatButton}
                onClick={onNewChat}
                disabled={isLoading}
                aria-label={`New chat in ${newChatTargetWorkspaceName}`}
              >
                <Add12Filled />
                <span className={styles.newChatLabel}>New chat</span>
              </button>
            </Tooltip>
          </div>

          <Tooltip
            content="Show only favorites (Keyboard: P)"
            relationship="label"
            showDelay={300}
            hideDelay={100}
          >
            <button
              className={mergeClasses(
                sharedPillButtonStyles.pillButton,
                styles.pillButton,
                isFavorited && sharedPillButtonStyles.pillButtonActive,
              )}
              onClick={() => onFavoritedChange(!isFavorited)}
              disabled={isLoading}
              aria-pressed={isFavorited}
              aria-label={
                isFavorited ? "Remove favorites filter" : "Show only favorites"
              }
            >
              {isFavorited ? (
                <StarFilled className={styles.filterIcon} />
              ) : (
                <StarRegular className={styles.filterIcon} />
              )}
              <span className={styles.label}>Favorites</span>
            </button>
          </Tooltip>

          <div
            className={mergeClasses(
              sortStyles.sortGroup,
              styles.sortGroupRight,
            )}
          >
            <span id="sort-by-label-chats" className={sortStyles.sortLabel}>
              Sort by:
            </span>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="outline"
                  className={sortStyles.sortMenuButton}
                  aria-labelledby="sort-by-label-chats"
                >
                  {sortField === "name" ? "Name" : "Last Updated"}
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => onSortFieldChange("name")}>
                    Name
                  </MenuItem>
                  <MenuItem onClick={() => onSortFieldChange("updatedAt")}>
                    Last Updated
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Tooltip
              content={
                isAscending
                  ? "Currently ascending, sort descending (Keyboard: S)"
                  : "Currently descending, sort ascending (Keyboard: S)"
              }
              relationship="label"
              appearance="inverted"
            >
              <Button
                appearance="subtle"
                icon={
                  isAscending ? (
                    <ArrowSortDownLinesRegular />
                  ) : (
                    <ArrowSortUpLinesRegular />
                  )
                }
                className={sortStyles.sortDirectionButton}
                onClick={() => onSortChange(!isAscending)}
                disabled={isLoading}
                aria-label={
                  isAscending
                    ? "Currently ascending, sort descending"
                    : "Currently descending, sort ascending"
                }
              />
            </Tooltip>
          </div>
        </div>
      </div>

      {newChatTargetWorkspaceName && (
        <div className={styles.hintText} aria-live="polite">
          <span>New chats will be created in {newChatTargetWorkspaceName}</span>
        </div>
      )}
    </div>
  );
};
